import { NButton, NIcon, NLink, NTooltip } from '@innersource-nn/novoui-react';
import ExpandableCard from 'components/card/ExpandableCard';
import SimpleLoader from 'components/loader/SimpleLoader';
import DOMPurify from 'dompurify';

export interface DisplayData {
    label: string;
    value: string | string[];
    uri?: string;
}
export interface ItemsDetails {
    created?: string;
    label?: string;
    description?: string;
    data: DisplayData[];
}
interface EditorDetailsProps {
    loading?: boolean;
    error: boolean;
    items: ItemsDetails;
    type: string;
    hideDescription?: boolean;
    expandable?: boolean;
    explandableTitle?: string;
    className?: string;
}
const EditorDetails = ({
    loading,
    error,
    items,
    type,
    hideDescription,
    expandable,
    explandableTitle,
    className,
}: EditorDetailsProps) => {
    const sanitizedHtml = items.description
        ? DOMPurify.sanitize(items.description)
        : '';
    const ContentGrid = () => {
        return (
            Object.keys(items.data)?.length !== 0 && (
                <div
                    className={`grid ${className ? className : 'grid-cols-3 xl:grid-cols-4'}  gap-24 mt-16`}
                >
                    {items.data.map((field, index) => (
                        <div key={index}>
                            <b>{field.label}</b>
                            <div className="font-extralight">
                                {field.uri ? (
                                    <NLink href={field.uri}>
                                        {field.value}
                                    </NLink>
                                ) : // If the value or the array is empty we will show -
                                !field.value || !field.value.length ? (
                                    '-'
                                ) : // If there is a value or the array has only 1 element we will show the value
                                typeof field.value === 'string' ||
                                  field.value.length === 1 ? (
                                    <div className="break-all ">
                                        {field.value}
                                    </div>
                                ) : (
                                    // When there is an array with more than 1 element we will add a button to show the values
                                    <NTooltip
                                        clickToggle
                                        tooltipChildren={
                                            <div>
                                                {field.value.map(
                                                    (value, index) => (
                                                        <div key={index}>
                                                            {value}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        }
                                        indicator
                                        placement="up"
                                        variant="default"
                                    >
                                        <NButton
                                            square
                                            size="tiny"
                                            variant="outline"
                                            className="mt-4"
                                        >
                                            See all ({field.value.length})
                                        </NButton>
                                    </NTooltip>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )
        );
    };

    return (
        <div>
            {loading ? (
                <SimpleLoader />
            ) : (
                !error &&
                Object.keys(items).length !== 0 && (
                    <div className="text-true-blue flex flex-col gap-y-16">
                        <div>
                            {!hideDescription && (
                                <div>
                                    <h3 className="text-2xl font-bold">
                                        Description
                                    </h3>

                                    {!items.description ? (
                                        <i className="text-faded-blue-600 text-sm font-extralight">
                                            This {type} does not have a
                                            description
                                        </i>
                                    ) : (
                                        <div
                                            className="description-link" // Apply styles using a CSS class
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizedHtml,
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        {expandable ? (
                            <ExpandableCard
                                title={explandableTitle ?? 'Details'}
                                icon={
                                    <NIcon
                                        iconName="information"
                                        size={24}
                                        className="fill-true-blue"
                                    />
                                }
                                content={<ContentGrid />}
                                defaultOpen={true}
                            />
                        ) : (
                            <ContentGrid />
                        )}
                    </div>
                )
            )}
        </div>
    );
};

export default EditorDetails;
